import { FC, PropsWithChildren, useContext, useRef, useState } from 'react';
import { Box } from '@zoopla-eng/zds-components/box';
import { SearchContext } from '@zoopla/listing-search-context';
import { MobileQuickSearchButton } from '@zoopla/listing-search/button';
import { FiltersModal } from '@zoopla/listing-search/filters/modal';
import { ModalManager } from '@zoopla/listing-search/modal-manager';
import { SearchInitiation } from '@zoopla/listing-search/search-initiation';
import {
    CheckboxPropertyType,
    CheckboxPropertyTypeShowAll,
    guiltyHackForceElementsInline,
    hideElement,
    InputLocation,
    SearchBar,
    SearchBarLayoutElement,
    SearchBarLayout,
    SearchBarShadow,
    SearchButton,
    SelectBeds,
    SelectDistance,
    SelectGroupBeds,
    SelectGroupPriceRange,
    SelectGroupProperty,
    SelectMaxBeds,
    SelectMinBeds,
    SelectPriceMax,
    SelectPriceMin,
    SelectPriceRange,
    SelectPropertyType,
    SelectRentalPriceFrequency,
    useSearchBarExperiment
} from '@zoopla/listing-search/listing-search-bar';
import { triggerTracking } from '@zoopla/tracking';
import {
    desktopSubHeaderWrapperStyle,
    locationWrapperStyle,
    searchInputWrapperStyle,
    stickyDesktopSubHeaderDynamicShadowStyle,
    stickyDesktopSubHeaderWrapperStyle,
    stickyQuickSearchBarDynamicShadowStyle,
    stickyQuickSearchBarWrapperStyle,
    styledMobileQuickSearchBarStyle
} from './Layout.styles.css';
import { desktopFiltersButtonWrapperStyle } from './SearchAndFiltersWrapper.styles.css';

const mobileModalUpdateResultsText = 'Update results';

type SearchAndFiltersWrapperProps = {
    showFiltersOnDesktop?: boolean;
};

const SearchAndFiltersWrapper: FC<
    PropsWithChildren<SearchAndFiltersWrapperProps>
> = ({ children, showFiltersOnDesktop = false }) => {
    const { state } = useContext(SearchContext);

    const container = useRef<HTMLDivElement>(null);

    const [isModalShowing, setIsModalShowing] = useState(false);

    const openModal = () => {
        setIsModalShowing(true);
    };

    const closeModal = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'search_filters',
            eventAction: 'click',
            eventLabel: 'quick_search_bar_close'
        });

        setIsModalShowing(false);
    };

    const { searchType } = state;

    /*
    Todo: cleanup optimizely experiment once complete
    ---
    SR-1932 Display Recent locations and categorise autosuggestions within search initiation
    */
    const isUsingSearchBar = useSearchBarExperiment();

    return isUsingSearchBar ? (
        <Box fw ref={container}>
            {showFiltersOnDesktop ? null : <SearchBarShadow />}
            <SearchBar
                automaticUpdate
                tracking={{
                    search: () => {
                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: 'search results',
                            eventAction: 'click',
                            eventLabel: `${searchType}-success`
                        });
                    }
                }}
            >
                <SearchBarLayout
                    layout="lsrp"
                    isFullWidthPage={showFiltersOnDesktop}
                >
                    <SearchBarLayoutElement type="input">
                        <InputLocation
                            id="header-location"
                            label="Enter a location"
                        />
                    </SearchBarLayoutElement>
                    <SearchBarLayoutElement
                        type="distance"
                        className={hideElement({
                            size: 'small'
                        })}
                    >
                        <SelectDistance />
                    </SearchBarLayoutElement>
                    <SearchBarLayoutElement
                        type="bedrooms"
                        className={hideElement({
                            size: 'medium'
                        })}
                    >
                        <SelectBeds>
                            <SelectGroupBeds>
                                <SelectMinBeds />
                                <SelectMaxBeds />
                            </SelectGroupBeds>
                        </SelectBeds>
                    </SearchBarLayoutElement>
                    <SearchBarLayoutElement
                        type="price"
                        className={hideElement({
                            size: 'medium'
                        })}
                    >
                        <SelectPriceRange>
                            <SelectGroupPriceRange>
                                <SelectPriceMin />
                                <SelectPriceMax />
                                <SelectRentalPriceFrequency />
                            </SelectGroupPriceRange>
                        </SelectPriceRange>
                    </SearchBarLayoutElement>
                    <SearchBarLayoutElement
                        type="propertyType"
                        className={hideElement({ size: 'medium' })}
                    >
                        <SelectPropertyType>
                            <SelectGroupProperty align="right">
                                <Box
                                    rowGap="S200"
                                    display="flex"
                                    flexDirection="column"
                                >
                                    <CheckboxPropertyTypeShowAll />
                                    <CheckboxPropertyType name="bungalow" />
                                    <CheckboxPropertyType name="detached" />
                                    <CheckboxPropertyType name="farms_land" />
                                </Box>
                                <Box
                                    rowGap="S200"
                                    display="flex"
                                    flexDirection="column"
                                >
                                    <CheckboxPropertyType name="semi_detached" />
                                    <CheckboxPropertyType name="terraced" />
                                    <CheckboxPropertyType name="flats" />
                                </Box>
                            </SelectGroupProperty>
                        </SelectPropertyType>
                    </SearchBarLayoutElement>
                    <SearchBarLayoutElement
                        type="filter"
                        className={guiltyHackForceElementsInline({
                            inline: true
                        })}
                    >
                        <FiltersModal container={container} />
                    </SearchBarLayoutElement>
                    <SearchBarLayoutElement
                        type="search"
                        className={`${hideElement({
                            size: 'small'
                        })} ${guiltyHackForceElementsInline({ inline: true })}`}
                    >
                        <SearchButton />
                    </SearchBarLayoutElement>
                </SearchBarLayout>
            </SearchBar>
            {children}
            <ModalManager
                modalProps={{
                    updateResultsText: mobileModalUpdateResultsText,
                    searchType,
                    eventCategory: 'search results',
                    searchSource: 'facets'
                }}
                isModalShowing={isModalShowing}
                closeModal={closeModal}
                container={container}
            />
        </Box>
    ) : (
        <Box fw ref={container}>
            <div
                className={stickyQuickSearchBarDynamicShadowStyle}
                data-testid="search-and-filters"
            >
                <nav className={stickyQuickSearchBarWrapperStyle}>
                    <div className={styledMobileQuickSearchBarStyle}>
                        <div className={locationWrapperStyle}>
                            <MobileQuickSearchButton
                                openModal={openModal}
                                searchType={searchType}
                            />
                        </div>
                        <FiltersModal container={container} />
                    </div>
                </nav>
            </div>
            {showFiltersOnDesktop && (
                <div
                    className={desktopSubHeaderWrapperStyle({
                        isStickyDesktopBar: false
                    })}
                    data-testid="search-desktop-subheader"
                >
                    <div className={searchInputWrapperStyle}>
                        <SearchInitiation
                            searchType={searchType}
                            eventCategory="search results"
                            searchSource="refine"
                        />
                    </div>

                    <div className={desktopFiltersButtonWrapperStyle}>
                        <FiltersModal container={container} />
                    </div>
                </div>
            )}
            {!showFiltersOnDesktop && (
                <div
                    className={stickyDesktopSubHeaderDynamicShadowStyle}
                    data-testid="search-and-filters"
                >
                    <div className={stickyDesktopSubHeaderWrapperStyle}>
                        <div
                            className={desktopSubHeaderWrapperStyle({
                                isStickyDesktopBar: true
                            })}
                            data-testid="search-desktop-subheader"
                        >
                            <div className={searchInputWrapperStyle}>
                                <SearchInitiation
                                    searchType={searchType}
                                    eventCategory="search results"
                                    searchSource="refine"
                                />
                            </div>
                            <div className={desktopFiltersButtonWrapperStyle}>
                                <FiltersModal container={container} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {children}
            <ModalManager
                modalProps={{
                    updateResultsText: mobileModalUpdateResultsText,
                    // enableSearchTypeTabs: false,
                    searchType,
                    eventCategory: 'search results',
                    searchSource: 'facets'
                }}
                isModalShowing={isModalShowing}
                closeModal={closeModal}
                container={container}
            />
        </Box>
    );
};

export default SearchAndFiltersWrapper;
